import React, { useState, useEffect } from 'react';
import { invDashboardService } from '../../services/inventoryService';
import { card, pageWrap, fmtCost, fmtQty, fmtDateTime, statusBadge, btn, emptyState } from './inventoryStyles';

const KPICard = ({ label, value, sub, color = '#FF6B00' }) => (
  <div style={{ ...card, padding: '20px 24px', flex: 1, minWidth: '160px' }}>
    <div style={{ fontSize: '1.8rem', fontWeight: '900', color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '4px', fontWeight: '600' }}>{label}</div>
    {sub && <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '2px' }}>{sub}</div>}
  </div>
);

const MOVEMENT_LABELS = {
  opening_stock:              'Opening Stock',
  purchase_inward:            'Stock Received',
  manual_adjustment_increase: 'Adjustment (+)',
  manual_adjustment_decrease: 'Adjustment (−)',
  recipe_consumption:         'Sale Deduction',
  wastage:                    'Wastage',
  stock_audit_variance:       'Audit Variance',
  billed_order_reversal:      'Invoice Void (Restored)',
  cancelled_inward_reversal:  'Inward Cancelled',
};

const InventoryDashboard = ({ restaurant, onNavigate }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const restaurantId = restaurant?.id;

  useEffect(() => {
    if (!restaurantId) return;
    invDashboardService.getSummary(restaurantId).then(({ data }) => {
      setSummary(data);
      setLoading(false);
    });
  }, [restaurantId]);

  if (loading) {
    return (
      <div style={{ ...pageWrap, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '44px', height: '44px', border: '4px solid #FF4444', borderTop: '4px solid transparent', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
          <p style={{ color: '#666', fontWeight: '600' }}>Loading inventory…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '800', color: '#111' }}>Inventory Dashboard</h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.9rem' }}>
            Real-time stock overview for {restaurant?.name}
          </p>
        </div>

        {/* KPI Row */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '28px' }}>
          <KPICard label="Total Stock Items"  value={summary?.totalItems ?? 0}     color="#111" />
          <KPICard label="Total Stock Value"  value={fmtCost(summary?.totalValue)}  color="#16a34a" />
          <KPICard label="Low Stock Items"    value={summary?.lowStockCount ?? 0}   color="#c2410c"
            sub={summary?.lowStockCount > 0 ? 'Needs reorder' : 'All good'} />
          <KPICard label="Out of Stock"       value={summary?.outOfStockCount ?? 0} color="#dc2626"
            sub={summary?.outOfStockCount > 0 ? 'Immediate action' : 'None'} />
          <KPICard label="Weekly Wastage"     value={fmtCost(summary?.weeklyWastageValue)} color="#9333ea" />
        </div>

        {/* Two column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>

          {/* Low Stock Alert */}
          <div style={{ ...card, padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#333' }}>Low Stock Alert</h3>
              <button style={{ ...btn('outline'), padding: '5px 12px', fontSize: '0.78rem' }} onClick={() => onNavigate('stock-items')}>View All</button>
            </div>
            {(!summary?.lowStockItems || summary.lowStockItems.length === 0) ? (
              <div style={{ ...emptyState, padding: '24px' }}>All items well stocked</div>
            ) : summary.lowStockItems.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333' }}>{item.name}</div>
                  <div style={{ fontSize: '0.78rem', color: '#888' }}>Min: {fmtQty(item.min_stock_threshold)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: '700', color: '#c2410c', fontSize: '0.95rem' }}>{fmtQty(item.current_stock)}</div>
                  <span style={statusBadge('low_stock')}>Low Stock</span>
                </div>
              </div>
            ))}
          </div>

          {/* Out of Stock */}
          <div style={{ ...card, padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#333' }}>Out of Stock</h3>
              <button style={{ ...btn('primary'), padding: '5px 12px', fontSize: '0.78rem' }} onClick={() => onNavigate('purchase-orders')}>Create PO</button>
            </div>
            {(!summary?.outOfStockItems || summary.outOfStockItems.length === 0) ? (
              <div style={{ ...emptyState, padding: '24px' }}>No items out of stock</div>
            ) : summary.outOfStockItems.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333' }}>{item.name}</div>
                <span style={statusBadge('out_of_stock')}>Out of Stock</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

          {/* Recent Movements */}
          <div style={{ ...card, padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#333' }}>Recent Stock Movements</h3>
              <button style={{ ...btn('outline'), padding: '5px 12px', fontSize: '0.78rem' }} onClick={() => onNavigate('reports')}>Full Report</button>
            </div>
            {(!summary?.recentMovements || summary.recentMovements.length === 0) ? (
              <div style={{ ...emptyState, padding: '24px' }}>No movements yet</div>
            ) : summary.recentMovements.map(mov => (
              <div key={mov.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.88rem', color: '#333' }}>{mov.inventory_item?.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>{MOVEMENT_LABELS[mov.movement_type] || mov.movement_type} · {fmtDateTime(mov.created_at)}</div>
                </div>
                <div style={{ fontWeight: '700', fontSize: '0.92rem', color: mov.quantity_change > 0 ? '#16a34a' : '#dc2626' }}>
                  {mov.quantity_change > 0 ? '+' : ''}{fmtQty(mov.quantity_change)}
                </div>
              </div>
            ))}
          </div>

          {/* Pending POs */}
          <div style={{ ...card, padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#333' }}>Pending Purchase Orders</h3>
              <button style={{ ...btn('primary'), padding: '5px 12px', fontSize: '0.78rem' }} onClick={() => onNavigate('purchase-orders')}>View All</button>
            </div>
            {(!summary?.pendingPOs || summary.pendingPOs.length === 0) ? (
              <div style={{ ...emptyState, padding: '24px' }}>No pending orders</div>
            ) : summary.pendingPOs.map(po => (
              <div key={po.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.88rem', color: '#333' }}>{po.po_number}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>{fmtDateTime(po.created_at)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: '700', fontSize: '0.88rem' }}>{fmtCost(po.total_amount)}</div>
                  <span style={statusBadge(po.status)}>{po.status.replace('_', ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ ...card, padding: '20px', marginTop: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: '700', color: '#333' }}>Quick Actions</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { label: '+ Add Stock',        tab: 'inward',           variant: 'primary' },
              { label: '+ Record Wastage',   tab: 'wastage',          variant: 'orange' },
              { label: '+ Create PO',        tab: 'pos',              variant: 'green' },
              { label: 'Stock Count',        tab: 'audit',            variant: 'outline' },
              { label: 'Reports',            tab: 'reports',          variant: 'outline' },
            ].map(a => (
              <button key={a.tab} style={btn(a.variant)} onClick={() => onNavigate(a.tab)}>{a.label}</button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default InventoryDashboard;
